import type { FC } from "react";
import { Box, Button, Container, Stack, Typography, Chip } from "@mui/material";
import { motion } from "framer-motion";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import BoltIcon from "@mui/icons-material/Bolt";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

export const HomeHero: FC = () => {
  return (
    <Box
      component="section"
      id="hero"
      sx={{
        background: "linear-gradient(145deg, #06040F 0%, #0C0A22 55%, #08091A 100%)",
        color: "#FFF",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
        pt: { xs: 14, md: 10 },
        pb: { xs: 10, md: 4 },
      }}
    >
      {/* Fine grid */}
      <Box sx={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage:
          "linear-gradient(rgba(129, 140, 248, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(129, 140, 248, 0.1) 1px, transparent 1px)",
        backgroundSize: "55px 55px",
        zIndex: 0,
      }} />

      {/* Vivid glow blobs */}
      <Box sx={{
        position: "absolute", top: "10%", left: "-5%",
        width: 560, height: 560, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(109, 40, 217, 0.35) 0%, transparent 65%)",
        filter: "blur(80px)", zIndex: 0, pointerEvents: "none",
      }} />
      <Box sx={{
        position: "absolute", bottom: "5%", right: "-5%",
        width: 460, height: 460, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(6, 182, 212, 0.28) 0%, transparent 65%)",
        filter: "blur(80px)", zIndex: 0, pointerEvents: "none",
      }} />
      <Box sx={{
        position: "absolute", top: "55%", left: "45%",
        width: 340, height: 340, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(99, 102, 241, 0.22) 0%, transparent 65%)",
        filter: "blur(70px)", zIndex: 0, pointerEvents: "none",
      }} />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          gap: { xs: 6, md: 8 },
        }}>
          {/* Left */}
          <Box sx={{ flex: 1, maxWidth: { md: "56%" } }}>
            <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
              <Chip
                label="Trusted OTC & Remittance Platform"
                sx={{
                  mb: 3,
                  bgcolor: "rgba(99, 102, 241, 0.18)",
                  color: "#A5B4FC",
                  border: "1px solid rgba(129, 140, 248, 0.45)",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  letterSpacing: "0.07em",
                }}
              />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.1 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.4rem", sm: "3.2rem", md: "3.8rem" },
                  fontWeight: 800,
                  lineHeight: 1.1,
                  mb: 3,
                  letterSpacing: "-0.025em",
                }}
              >
                Global{" "}
                <Box component="span" sx={{
                  background: "linear-gradient(135deg, #818CF8 0%, #C4B5FD 55%, #22D3EE 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}>
                  Remittance
                </Box>{" "}
                &{" "}
                <Box component="span" sx={{
                  background: "linear-gradient(135deg, #FCD34D 0%, #FBBF24 60%, #F97316 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}>
                  OTC
                </Box>{" "}
                Exchange
              </Typography>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.18 }}>
              <Typography sx={{
                fontSize: { xs: "1rem", md: "1.1rem" },
                lineHeight: 1.8,
                color: "rgba(255,255,255,0.7)",
                mb: 4.5, maxWidth: 520,
              }}>
                Move money across 30+ countries at lightning speed. Platinx Exchange delivers
                institutional-grade OTC trading and seamless international remittance
                with the most competitive rates — zero hidden fees.
              </Typography>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.26 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={5}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  href="mailto:support@platinx.exchange"
                  sx={{
                    background: "linear-gradient(135deg, #4F46E5 0%, #6366F1 50%, #818CF8 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    px: 4, py: 1.6,
                    fontSize: "1rem", borderRadius: 2,
                    boxShadow: "0 6px 28px rgba(99, 102, 241, 0.55)",
                    border: "1px solid rgba(165, 180, 252, 0.3)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #4338CA 0%, #4F46E5 100%)",
                      boxShadow: "0 10px 36px rgba(99, 102, 241, 0.72)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Start Transferring
                </Button>
                <Button
                  variant="outlined"
                  href="tel:+919560107106"
                  sx={{
                    color: "rgba(255,255,255,0.9)",
                    borderColor: "rgba(255,255,255,0.25)",
                    px: 4, py: 1.6,
                    fontSize: "1rem", borderRadius: 2,
                    "&:hover": {
                      borderColor: "#A5B4FC",
                      color: "#A5B4FC",
                      bgcolor: "rgba(99, 102, 241, 0.1)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  OTC Trading Desk
                </Button>
              </Stack>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.4 }}>
              <Stack direction="row" spacing={3.5} flexWrap="wrap" useFlexGap>
                {[
                  { icon: <VerifiedUserIcon sx={{ fontSize: 15 }} />, label: "RBI Compliant" },
                  { icon: <BoltIcon sx={{ fontSize: 15 }} />, label: "Instant Transfers" },
                  { icon: <TrendingUpIcon sx={{ fontSize: 15 }} />, label: "Best Rates" },
                ].map((b) => (
                  <Stack key={b.label} direction="row" spacing={0.75} alignItems="center" sx={{ color: "rgba(255,255,255,0.65)" }}>
                    {b.icon}
                    <Typography variant="caption" sx={{ color: "inherit", fontWeight: 600, fontSize: "0.78rem" }}>
                      {b.label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </motion.div>
          </Box>

          {/* Right — exchange card */}
          <Box sx={{
            flex: 1,
            display: { xs: "none", md: "flex" },
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
          }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.38 }}
              style={{ width: "100%", maxWidth: 380 }}
            >
              <Box sx={{
                background: "rgba(255,255,255,0.055)",
                backdropFilter: "blur(28px)",
                WebkitBackdropFilter: "blur(28px)",
                border: "1px solid rgba(129, 140, 248, 0.4)",
                borderRadius: 4, p: 4,
                boxShadow: "0 24px 70px rgba(0,0,0,0.55), 0 0 60px rgba(99, 102, 241, 0.12), inset 0 1px 0 rgba(255,255,255,0.08)",
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700 }}>
                    Live Exchange Rate
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{
                      width: 7, height: 7, borderRadius: "50%", bgcolor: "#4ADE80",
                      boxShadow: "0 0 10px #4ADE80",
                      animation: "blink 2s ease-in-out infinite",
                      "@keyframes blink": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
                    }} />
                    <Typography sx={{ color: "#4ADE80", fontSize: "0.7rem", fontWeight: 700 }}>Live</Typography>
                  </Stack>
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
                  <Box>
                    <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.66rem", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.1em" }}>You Send</Typography>
                    <Typography variant="h4" sx={{ color: "#fff", fontWeight: 800, lineHeight: 1, fontSize: "1.7rem" }}>₹1,00,000</Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.38)", fontSize: "0.72rem", mt: 0.5 }}>INR • India</Typography>
                  </Box>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: "50%",
                    background: "linear-gradient(135deg, #4F46E5, #22D3EE)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", fontWeight: 800, fontSize: "1.1rem",
                    boxShadow: "0 4px 18px rgba(99, 102, 241, 0.55)", flexShrink: 0,
                  }}>
                    ⇄
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.66rem", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.1em" }}>They Receive</Typography>
                    <Typography variant="h4" sx={{ color: "#FCD34D", fontWeight: 800, lineHeight: 1, fontSize: "1.7rem" }}>$1,198</Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.38)", fontSize: "0.72rem", mt: 0.5 }}>USD • USA</Typography>
                  </Box>
                </Stack>

                <Box sx={{
                  bgcolor: "rgba(99, 102, 241, 0.1)",
                  border: "1px solid rgba(129, 140, 248, 0.22)",
                  borderRadius: 2, p: 1.5, mb: 3,
                }}>
                  {[
                    { label: "Rate", value: "1 USD = ₹83.47", color: "#A5B4FC" },
                    { label: "Fee", value: "₹0", color: "#4ADE80" },
                    { label: "Time", value: "Instant", color: "#4ADE80" },
                  ].map((row) => (
                    <Stack key={row.label} direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem" }}>{row.label}</Typography>
                      <Typography sx={{ color: row.color, fontSize: "0.75rem", fontWeight: 700 }}>{row.value}</Typography>
                    </Stack>
                  ))}
                </Box>

                <Button fullWidth variant="contained" href="mailto:support@platinx.exchange" sx={{
                  background: "linear-gradient(135deg, #4F46E5 0%, #818CF8 100%)",
                  color: "#fff", fontWeight: 700, borderRadius: 2, py: 1.3,
                  boxShadow: "0 4px 18px rgba(99, 102, 241, 0.5)",
                  "&:hover": { background: "linear-gradient(135deg, #4338CA 0%, #4F46E5 100%)" },
                }}>
                  Transfer Now
                </Button>
              </Box>
            </motion.div>

            {/* Floating chips */}
            {[
              { style: { top: "-8%", left: "-20%" }, value: "30+", label: "Countries", delay: 0.7 },
              { style: { bottom: "2%", right: "-16%" }, value: "₹500Cr+", label: "Volume", delay: 0.9 },
            ].map((chip) => (
              <motion.div
                key={chip.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: chip.delay }}
                style={{ position: "absolute", ...chip.style }}
              >
                <Box sx={{
                  background: "rgba(255,255,255,0.07)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid rgba(129, 140, 248, 0.38)",
                  borderRadius: 3, px: 2.5, py: 1.5,
                  textAlign: "center", minWidth: 110,
                  boxShadow: "0 8px 24px rgba(99, 102, 241, 0.18)",
                }}>
                  <Typography variant="h5" sx={{ color: "#A5B4FC", fontWeight: 800, lineHeight: 1 }}>{chip.value}</Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.7rem", mt: 0.5 }}>{chip.label}</Typography>
                </Box>
              </motion.div>
            ))}
          </Box>
        </Box>

        {/* Stats bar */}
        <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.6 }}>
          <Box sx={{
            mt: { xs: 8, md: 10 },
            pt: 4,
            borderTop: "1px solid rgba(129, 140, 248, 0.18)",
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: 3,
          }}>
            {[
              { value: "30+", label: "Countries Served" },
              { value: "₹500Cr+", label: "Volume Processed" },
              { value: "10,000+", label: "Happy Clients" },
              { value: "99.9%", label: "Uptime SLA" },
            ].map((s) => (
              <Box key={s.label} sx={{ textAlign: "center" }}>
                <Typography variant="h4" sx={{ color: "#818CF8", fontWeight: 800, mb: 0.5, fontSize: { xs: "1.6rem", md: "2rem" } }}>
                  {s.value}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.48)", fontSize: "0.8rem" }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};
